import { useEffect, useState, useRef } from "react";
import FilePicker from "@/components/FilePicker";
import MessageFiles from "@/components/MessageFiles";
import { NavButtons } from "../components/NavButtons";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getCached, setCached } from "@/lib/cache";
import PageLoader from "@/components/PageLoader";
import { useTheme } from "@/context/ThemeContext";
import StatusBadge from "@/components/StatusBadge";
import PaymentCalendar from "@/components/PaymentCalendar";
import Tabs from "@/components/Tabs";
import FilterPills from "@/components/FilterPills";
import MarketingSection from "@/components/MarketingSection";
import NotesSection from "@/components/NotesSection";
import AccordionCard, { ExpandAllToggle } from "@/components/AccordionCard";
import { BulkBar, SelectBox, useBulkSelection } from "@/components/BulkSelect";
import RequiredActionsPanel from "@/components/RequiredActionsPanel";
import ProjectStatusBox, { isProjectStatusEligible } from "@/components/ProjectStatusBox";
import useDraft from "@/hooks/useDraft";
import TaskNotes from "@/components/TaskNotes";
import SampleGallery from "@/components/SampleGallery";
import AdminPaymentsList from "@/components/AdminPaymentsList";
import TaskFilterBar, { EMPTY_TASK_FILTER, applyTaskFilter } from "@/components/TaskFilterBar";

import AdminThreadReplyBox from "@/components/AdminThreadReplyBox";
import SystemMessagesList from "@/components/SystemMessagesList";
import MessageTemplatePicker from "@/components/MessageTemplatePicker";
import { CreatedCredentialsModal } from "@/pages/AdminDashboard";
import { LinkifiedText, FormatBar } from "@/components/LinkifiedText";
import MessageSearch from "@/components/MessageSearch";
import { splitMessages, sampleFilesOf } from "@/lib/messages";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, ShieldCheck, Trash, X, DoorOpen, UploadSimple, Archive, ArrowCounterClockwise,
  PaperPlaneTilt, DownloadSimple, CheckCircle, Check, PushPin, Sun, Moon, Sparkle,
  FileArrowDown, FloppyDisk, PencilSimple, CaretUp, CaretDown,
  Megaphone, ChatCircleDots, ThumbsUp, ThumbsDown, ClockClockwise,
  Eye, EyeSlash, Bell,
} from "@phosphor-icons/react";

const KINDS = ["link", "jotform", "acuity", "upload", "custom"];
const STATUSES = ["pending", "complete", "needs_verification"];

// Reusable due-date badge for samples (parses YYYY-MM-DD in local time)
function SampleDueBadge({ dueDate }) {
  if (!dueDate) return null;
  const iso = String(dueDate).slice(0, 10);
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  const due = new Date(y, m - 1, d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((due - today) / 86400000);
  let bg = "#dbeafe", color = "#1e3a8a";
  let label = `Due ${due.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  if (days < 0) { bg = "#fee2e2"; color = "#991b1b"; label += ` · Overdue ${-days}d`; }
  else if (days === 0) { bg = "#fef3c7"; color = "#78350f"; label += " · Today"; }
  else if (days <= 3) { bg = "#fef3c7"; color = "#78350f"; label += ` · in ${days}d`; }
  else { label += ` · in ${days}d`; }
  return (
    <span className="label px-2 py-0.5"
      style={{ background: bg, color, border: `1px solid ${bg}`, fontWeight: 700 }}>
      {label}
    </span>
  );
}

// ---------------- Tasks section (onboarding OR offboarding) ----------------
function _relativeTime(iso) {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

function TasksSection({ userId, tasks, sectionKey, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [allOpen, setAllOpen] = useState(null);
  const [tf, setTf] = useState(EMPTY_TASK_FILTER);
  const shown = applyTaskFilter(tasks, tf);
  const sel = useBulkSelection();
  const toggleArchive = async (t) => {
    try {
      await api.patch(`/admin/clients/${userId}/tasks/${t.task_id}`, { archived: !t.archived });
      toast.success(t.archived ? "Task restored" : "Task archived");
      onRefresh();
    } catch (_e) { toast.error("Update failed"); }
  };
  const setStatus = async (task_id, status) => {
    try {
      await api.patch(`/admin/clients/${userId}/tasks/${task_id}`, { status });
      toast.success("Task updated");
      onRefresh();
    } catch (_e) { toast.error("Update failed"); }
  };
  const saveTask = async (task_id, patch) => {
    try {
      await api.patch(`/admin/clients/${userId}/tasks/${task_id}`, patch);
      toast.success("Task saved");
      setEditingId(null); onRefresh();
    } catch (_e) { toast.error("Save failed"); }
  };
  const del = async (task_id) => {
    if (!window.confirm("Delete this task?")) return;
    await api.delete(`/admin/clients/${userId}/tasks/${task_id}`);
    onRefresh();
  };
  const sendReminder = async (task_id) => {
    try {
      const { data } = await api.post(`/admin/clients/${userId}/tasks/${task_id}/reminder`);
      if (data.sent) toast.success("Reminder sent");
      else toast.warning(data.error || "Reminder recorded (email delivery deferred)");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reminder failed");
    }
  };
  const move = async (task_id, delta) => {
    const ids = tasks.map(t => t.task_id);
    const i = ids.indexOf(task_id);
    if (i < 0) return;
    const j = Math.max(0, Math.min(ids.length - 1, i + delta));
    if (i === j) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await api.patch(`/admin/clients/${userId}/tasks/reorder`,
                    { section: sectionKey, task_ids: ids });
    onRefresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <h3 className="title-xl text-2xl">{sectionKey === "offboarding" ? "Offboarding tasks" : "Tasks"}</h3>
        <div className="flex items-center gap-3">
          {tasks.length > 1 && (
            <ExpandAllToggle testid={`admin-tasks-expand-all-${sectionKey}`}
              allOpen={allOpen === true}
              onToggle={(next) => setAllOpen(next)} />
          )}
          <button data-testid={`admin-add-task-${sectionKey}`} onClick={() => setShowAdd(true)} className="btn-primary">
            <Plus size={14} weight="bold" /> Add step
          </button>
        </div>
      </div>
      <TaskFilterBar tasks={tasks} value={tf} onChange={setTf} testid={`admin-task-filter-${sectionKey}`}
        statuses={[["pending", "Pending"], ["needs_verification", "Needs verification"], ["complete", "Complete"]]} />
      <BulkBar testid={`admin-task-bulk-${sectionKey}`} noun="tasks" sel={sel} visibleIds={shown.map(t => t.task_id)}
        onApply={async (ids, archived) => { await api.post(`/admin/clients/${userId}/tasks/bulk-archive`, { ids, archived }); onRefresh(); }} />
      {tasks.length === 0 && (
        <div className="panel p-8 text-center label">No steps yet</div>
      )}
      {tasks.length > 0 && shown.length === 0 && (
        <div className="panel p-8 text-center label">No tasks match these filters</div>
      )}
      {shown.map((t, n) => { const i = tasks.indexOf(t); return editingId === t.task_id ? (
        <EditTaskCard key={t.task_id} task={t} onCancel={() => setEditingId(null)}
                       onSave={(patch) => saveTask(t.task_id, patch)} />
      ) : (
        <AccordionCard key={t.task_id} testid={`admin-task-${t.task_id}`} anchorId={`task-${t.task_id}`}
          forceOpen={allOpen === null ? undefined : allOpen}
          actions={<SelectBox checked={sel.selected.has(t.task_id)} onChange={(on) => sel.toggle(t.task_id, on)}
            testid={`admin-select-task-${t.task_id}`} />}
          header={
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <span className="task-num flex-shrink-0" style={{ fontSize: "1rem" }}>{String(n + 1).padStart(2, "0")}</span>
              <StatusBadge status={t.status} />
              {t.archived && <span className="label px-2 py-0.5 border" style={{ borderColor: "var(--hair)" }}>archived</span>}
              {t.recurrence && t.recurrence !== "none" && (
                <span className="label px-2 py-0.5" data-testid={`admin-task-rec-${t.task_id}`}
                  style={{ background: "#e0e7ff", color: "#3730a3", border: "1px solid #e0e7ff", fontWeight: 700 }}
                  title={`Recurs ${t.recurrence}`}>
                  ↻ {t.recurrence}
                </span>
              )}
              <h4 className="title-xl text-lg break-words" style={{ fontWeight: 700 }}>{t.title}</h4>
            </div>
          }>
          <div className="space-y-4">
            {t.description && (
              <p className="leading-relaxed break-words" style={{ color: "var(--text-dim)" }}>{t.description}</p>
            )}
            {t.url && (
              <a href={t.url} target="_blank" rel="noreferrer"
                 className="label underline underline-offset-4 inline-block break-all" style={{ color: "var(--text-dim)" }}>
                {t.url}
              </a>
            )}
            {(t.files || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {t.files.map(f => (
                  <span key={f.file_id} className="inline-flex items-center gap-1"
                    data-testid={`admin-onb-file-${f.file_id}`}>
                    <button type="button"
                      onClick={async () => {
                        try {
                          const res = await api.get(`/files/${f.file_id}/download`, { responseType: "blob" });
                          const url = URL.createObjectURL(res.data);
                          const a = document.createElement("a"); a.href = url; a.download = f.filename || "file"; a.click();
                          URL.revokeObjectURL(url);
                        } catch (_e) { toast.error("Download failed"); }
                      }}
                      className="btn-ghost text-xs">{f.filename}</button>
                    <button type="button"
                      onClick={async () => {
                        try {
                          await api.delete(`/admin/clients/${userId}/tasks/${t.task_id}/files/${f.file_id}`);
                          onRefresh();
                        } catch (_e) { toast.error("Failed"); }
                      }}
                      className="btn-ghost text-xs p-1"
                      data-testid={`admin-onb-file-del-${f.file_id}`}>
                      <Trash size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-col md:flex-row gap-2 md:items-center">
              <div className="flex gap-2">
                <button data-testid={`admin-task-up-${t.task_id}`} disabled={i === 0}
                  onClick={() => move(t.task_id, -1)} className="btn-ghost text-xs flex-1 md:flex-none disabled:opacity-30">
                  <CaretUp size={12} weight="bold" /> Up
                </button>
                <button data-testid={`admin-task-down-${t.task_id}`} disabled={i === tasks.length - 1}
                  onClick={() => move(t.task_id, 1)} className="btn-ghost text-xs flex-1 md:flex-none disabled:opacity-30">
                  <CaretDown size={12} weight="bold" /> Down
                </button>
              </div>
              <select
                data-testid={`admin-set-status-${t.task_id}`}
                value={t.status}
                onChange={(e) => setStatus(t.task_id, e.target.value)}
                className="px-3 py-2"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{s === "needs_verification" ? "confirming receipt" : s.replace("_", " ")}</option>)}
              </select>
              <div className="flex gap-2 flex-wrap items-center">
                {t.status !== "complete" && (
                  <>
                    <button data-testid={`admin-task-remind-${t.task_id}`}
                      onClick={() => sendReminder(t.task_id)}
                      className="btn-ghost text-xs"
                      title={t.last_reminded_at
                        ? `Last reminder: ${new Date(t.last_reminded_at).toLocaleString()}`
                        : "Send reminder email to client"}>
                      <Bell size={12} weight="bold" /> Remind
                    </button>
                    {t.last_reminded_at && (
                      <span className="label text-[10px] px-2 py-0.5"
                        data-testid={`admin-task-reminded-${t.task_id}`}
                        style={{
                          background: "#fef3c7",
                          color: "#78350f",
                          border: "1px solid #fde68a",
                          fontWeight: 700,
                        }}>
                        Reminded {_relativeTime(t.last_reminded_at)}
                      </span>
                    )}
                  </>
                )}
                <button data-testid={`admin-task-edit-${t.task_id}`} onClick={() => setEditingId(t.task_id)} className="btn-ghost text-xs flex-1 md:flex-none">
                  <PencilSimple size={12} weight="bold" /> Edit
                </button>
                <button data-testid={`admin-archive-task-${t.task_id}`} onClick={() => toggleArchive(t)} className="btn-ghost text-xs flex-1 md:flex-none">
                  {t.archived ? <><ArrowCounterClockwise size={12} weight="bold" /> Restore</> : <><Archive size={12} weight="bold" /> Archive</>}
                </button>
                <button data-testid={`admin-del-${t.task_id}`} onClick={() => del(t.task_id)} className="btn-ghost text-xs flex-1 md:flex-none">
                  <Trash size={12} weight="bold" /> Delete
                </button>
              </div>
            </div>
            <TaskNotes taskId={t.task_id} clientUserId={userId} uploadPath={`/admin/clients/${userId}/files`}
              initialNotes={t.notes || []} testid="admin-task-notes" />
          </div>
        </AccordionCard>
      ); })}
      {showAdd && <AddTaskModal userId={userId} section={sectionKey} onClose={() => setShowAdd(false)} onDone={onRefresh} />}
    </div>
  );
}

function EditTaskCard({ task, onSave, onCancel }) {
  const [f, setF] = useState({
    title: task.title || "", description: task.description || "",
    kind: task.kind || "link", url: task.url || "",
    due_date: (task.due_date || "").slice(0, 10),
    recurrence: task.recurrence || "none",
  });
  return (
    <div className="panel p-6 space-y-3" data-testid={`admin-task-edit-${task.task_id}`}>
      <p className="label">Edit step</p>
      <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
        data-testid={`edit-task-title-${task.task_id}`}
        placeholder="Title" className="w-full px-3 py-2" />
      <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
        data-testid={`edit-task-desc-${task.task_id}`}
        placeholder="Description" rows={2} className="w-full px-3 py-2" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}
          data-testid={`edit-task-kind-${task.task_id}`} className="px-3 py-2">
          {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <input value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })}
          data-testid={`edit-task-url-${task.task_id}`}
          placeholder="URL" className="px-3 py-2" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <p className="label mb-1">Due date (optional)</p>
          <input type="date" value={f.due_date}
            data-testid={`edit-task-due-${task.task_id}`}
            onChange={(e) => setF({ ...f, due_date: e.target.value })}
            className="w-full px-3 py-2" />
        </div>
        <div>
          <p className="label mb-1">Recurrence</p>
          <select value={f.recurrence}
            data-testid={`edit-task-recurrence-${task.task_id}`}
            onChange={(e) => setF({ ...f, recurrence: e.target.value })}
            className="w-full px-3 py-2">
            <option value="none">One-off</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Every 2 weeks</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
      </div>
      <div className="flex gap-2">
        <button data-testid={`edit-task-save-${task.task_id}`}
          onClick={() => onSave({ ...f, due_date: f.due_date || null })} className="btn-primary text-sm flex-1">
          <FloppyDisk size={12} weight="bold" /> Save
        </button>
        <button onClick={onCancel} className="btn-ghost text-sm flex-1">
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
}

function AddTaskModal({ userId, section, onClose, onDone }) {
  const [f, setF] = useState({ title: "", description: "", kind: "link", url: "", due_date: "", recurrence: "none", section });
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const filesRef = useRef(null);
  // Template picker state — pull an existing template step in one click
  const [templates, setTemplates] = useState([]);
  const [tplPickTemplate, setTplPickTemplate] = useState("");
  const [tplPickStep, setTplPickStep] = useState("");
  const [recentTplIds, setRecentTplIds] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem("recent-task-templates") || "[]");
      return Array.isArray(v) ? v.slice(0, 3) : [];
    } catch { return []; }
  });
  useEffect(() => {
    api.get("/admin/templates").then(r => setTemplates(r.data || [])).catch(() => {});
  }, []);
  const rememberTemplate = (tid) => {
    if (!tid) return;
    setRecentTplIds(prev => {
      const next = [tid, ...prev.filter(x => x !== tid)].slice(0, 3);
      try { localStorage.setItem("recent-task-templates", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const recentTemplates = recentTplIds
    .map(id => templates.find(t => t.template_id === id))
    .filter(Boolean);
  const otherTemplates = templates.filter(t => !recentTplIds.includes(t.template_id));
  const activeTpl = templates.find(t => t.template_id === tplPickTemplate);
  const activeSteps = [...(activeTpl?.tasks || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );
  const applyStep = (stepId) => {
    const step = activeSteps.find(s => s.step_id === stepId);
    if (!step) return;
    setF(cur => ({
      ...cur,
      title: step.title || cur.title,
      description: step.description || cur.description,
      kind: step.kind || cur.kind,
      url: step.url || cur.url,
    }));
    rememberTemplate(tplPickTemplate);
    toast.success(`Loaded "${step.title}" from ${activeTpl.name}`);
  };
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const payload = { ...f, due_date: f.due_date || null };
      if (saveAsTemplate && templateName.trim()) payload.save_as_template = templateName.trim();
      const { data: created } = await api.post(`/admin/clients/${userId}/tasks`, payload);
      // Upload attachments (if any) to the newly-created onboarding task
      const files = Array.from(filesRef.current?.files || []);
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/admin/clients/${userId}/tasks/${created.task_id}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(saveAsTemplate && templateName.trim() ? "Step added + saved to template" : "Step added");
      onDone(); onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="add-task-modal">
      <div className="panel w-full max-w-lg p-8">
        <div className="flex justify-between items-start mb-6">
          <h3 className="title-xl text-2xl">Add step</h3>
          <button onClick={onClose} data-testid="add-task-close" className="btn-ghost p-2"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {templates.length > 0 && (
            <div className="hair p-3 space-y-2" data-testid="add-task-tpl-panel">
              <p className="label" style={{ fontWeight: 700 }}>
                Use a task from a template <span style={{ color: "var(--text-dim)", fontWeight: 400 }}>(optional)</span>
              </p>
              <select value={tplPickTemplate} data-testid="add-task-tpl-select"
                onChange={(e) => { setTplPickTemplate(e.target.value); setTplPickStep(""); }}
                className="w-full px-3 py-2 text-sm">
                <option value="">— Choose a template —</option>
                {recentTemplates.length > 0 && (
                  <optgroup label="Recent">
                    {recentTemplates.map(t => (
                      <option key={`r-${t.template_id}`} value={t.template_id}>
                        ★ {t.name} · {(t.tasks || []).length} task(s)
                      </option>
                    ))}
                  </optgroup>
                )}
                <optgroup label={recentTemplates.length > 0 ? "All templates" : "Templates"}>
                  {otherTemplates.map(t => (
                    <option key={t.template_id} value={t.template_id}>
                      {t.name} · {(t.tasks || []).length} task(s)
                    </option>
                  ))}
                </optgroup>
              </select>
              {activeSteps.length > 0 && (
                <div className="flex gap-2">
                  <select value={tplPickStep} data-testid="add-task-tpl-step"
                    onChange={(e) => setTplPickStep(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm">
                    <option value="">— Pick a task —</option>
                    {activeSteps.map(s => (
                      <option key={s.step_id} value={s.step_id}>{s.title}</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => applyStep(tplPickStep)}
                    disabled={!tplPickStep}
                    data-testid="add-task-tpl-use"
                    className="btn-primary text-xs disabled:opacity-40">
                    Use
                  </button>
                </div>
              )}
            </div>
          )}
          <input required placeholder="Title" value={f.title} data-testid="task-input-title"
            onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full px-4 py-3" />
          <textarea placeholder="Description" value={f.description} data-testid="task-input-desc"
            onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full px-4 py-3" rows={3} />
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}
            data-testid="task-input-kind" className="w-full px-4 py-3">
            {["link", "jotform", "acuity", "upload", "custom"].map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <input placeholder="URL (link / jotform / acuity)" value={f.url} data-testid="task-input-url"
            onChange={(e) => setF({ ...f, url: e.target.value })} className="w-full px-4 py-3" />
          <div>
            <p className="label mb-1">Due date (optional)</p>
            <input type="date" value={f.due_date} data-testid="task-input-due"
              onChange={(e) => setF({ ...f, due_date: e.target.value })} className="w-full px-4 py-3" />
          </div>
          <div>
            <p className="label mb-1">Recurrence</p>
            <select value={f.recurrence} data-testid="task-input-recurrence"
              onChange={(e) => setF({ ...f, recurrence: e.target.value })} className="w-full px-4 py-3">
              <option value="none">One-off (no repeat)</option>
              <option value="daily">Repeat daily</option>
              <option value="weekly">Repeat weekly</option>
              <option value="biweekly">Repeat every 2 weeks</option>
              <option value="monthly">Repeat monthly</option>
              <option value="quarterly">Repeat quarterly</option>
              <option value="yearly">Repeat yearly</option>
            </select>
            {f.recurrence !== "none" && (
              <p className="label mt-1" style={{ color: "var(--text-dim)" }}>
                A new occurrence spawns automatically when this step is marked complete.
                {!f.due_date && " (Set a due date first — recurrence needs it to schedule the next one.)"}
              </p>
            )}
          </div>
          <div>
            <p className="label mb-1">Attachments (optional, multiple)</p>
            <FilePicker ref={filesRef} testid="task-input-files" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={saveAsTemplate} data-testid="task-save-template-check"
              onChange={(e) => setSaveAsTemplate(e.target.checked)} className="w-4 h-4" />
            Save this step to a template
          </label>
          {saveAsTemplate && (
            <input placeholder="Template name (creates or appends)" value={templateName}
              data-testid="task-template-name"
              onChange={(e) => setTemplateName(e.target.value)} className="w-full px-4 py-3" />
          )}
          {err && <p className="label px-3 py-2" style={{ background: "var(--invert-bg)", color: "var(--invert-text)" }}>{err}</p>}
          <button data-testid="task-submit-btn" disabled={busy} className="w-full btn-primary justify-center h-12">
            {busy ? "Adding…" : "Add step"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------- Client meta editor (sidebar version) ----------------
function ClientMeta({ userId, client, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(client.name || "");
  const [businessName, setBusinessName] = useState(client.business_name || "");
  const [phone, setPhone] = useState(client.phone || "");
  const [email, setEmail] = useState(client.email);
  const [eta, setEta] = useState((client.estimated_completion_date || "").slice(0, 10));
  const [types, setTypes] = useState(
    Array.isArray(client.client_types) && client.client_types.length > 0
      ? client.client_types
      : [client.client_type || "branding"]
  );
  const [busy, setBusy] = useState(false);
  const [busy2, setBusy2] = useState(false);

  const startEdit = () => {
    setName(client.name || "");
    setBusinessName(client.business_name || "");
    setPhone(client.phone || "");
    setEmail(client.email);
    setEta((client.estimated_completion_date || "").slice(0, 10));
    setTypes(
      Array.isArray(client.client_types) && client.client_types.length > 0
        ? client.client_types
        : [client.client_type || "branding"]
    );
    setEditing(true);
  };
  const toggleType = (t) => {
    setTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };
  const cancelEdit = () => setEditing(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/clients/${userId}`, {
        name: name.trim(),
        business_name: businessName.trim() || null,
        phone: phone.trim() || null,
        estimated_completion_date: eta || null,
        client_types: types.length > 0 ? types : ["branding"],
      });
      if (email !== client.email) {
        await api.patch(`/admin/clients/${userId}/email`, { email });
      }
      toast.success("Details updated");
      setEditing(false);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const sendReminder = async () => {
    setBusy2(true);
    try {
      const { data } = await api.post(`/admin/clients/${userId}/reminder-task`);
      if (data.sent) toast.success("Reminder sent"); else toast.warning(data.note || data.error || "Nothing to remind");
    } catch (_e) { toast.error("Failed"); }
    finally { setBusy2(false); }
  };
  const resetPw = async () => {
    if (!window.confirm(`Reset password for ${client.name}? A new temporary password will be emailed to them.`)) return;
    try {
      const { data } = await api.post(`/admin/clients/${userId}/reset-password`);
      toast.success(`New temp password: ${data.temporary_password}`);
    } catch (_e) { toast.error("Failed"); }
  };

  const TYPE_LABELS = { branding: "Branding", web_design: "Web Design", marketing: "Marketing" };
  const currentTypes = (
    Array.isArray(client.client_types) && client.client_types.length > 0
      ? client.client_types
      : [client.client_type]
  ).filter(Boolean);
  const clientTypeLabel = currentTypes.length
    ? currentTypes.map(t => TYPE_LABELS[t] || t).join(" · ")
    : "—";

  return (
    <div className="panel p-5" data-testid="client-meta-editor">
      <div className="flex items-center justify-between mb-3">
        <p className="label flex items-center gap-2"><PencilSimple size={12} weight="bold" /> Client details</p>
        {!editing && (
          <button onClick={startEdit} data-testid="meta-edit-btn"
            className="btn-ghost text-xs">
            <PencilSimple size={11} weight="bold" /> Edit
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-3">
          <div>
            <p className="label mb-1">Full name</p>
            <input value={name} onChange={(e) => setName(e.target.value)}
              data-testid="meta-name" className="w-full px-3 py-2 text-sm" />
          </div>
          <div>
            <p className="label mb-1">Business name</p>
            <input value={businessName} onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Optional"
              data-testid="meta-business" className="w-full px-3 py-2 text-sm" />
          </div>
          <div>
            <p className="label mb-1">Phone</p>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="Optional"
              data-testid="meta-phone" className="w-full px-3 py-2 text-sm" />
          </div>
          <div>
            <p className="label mb-1">Email</p>
            <input value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="meta-email" className="w-full px-3 py-2 text-sm font-mono" />
          </div>
          <div>
            <p className="label mb-1">Est. completion date</p>
            <input type="date" value={eta} onChange={(e) => setEta(e.target.value)}
              data-testid="meta-eta" className="w-full px-3 py-2 text-sm" />
          </div>
          <div>
            <p className="label mb-1">Client types (pick one or more)</p>
            <div className="flex flex-wrap gap-2" data-testid="meta-client-types">
              {[
                { k: "branding", label: "Branding" },
                { k: "web_design", label: "Web Design" },
                { k: "marketing", label: "Marketing" },
              ].map(t => {
                const on = types.includes(t.k);
                return (
                  <button key={t.k} type="button"
                    data-testid={`meta-type-${t.k}`}
                    onClick={() => toggleType(t.k)}
                    className={on ? "btn-primary text-xs" : "btn-ghost text-xs"}>
                    {on ? "✓ " : ""}{t.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2">
            <button data-testid="meta-save" disabled={busy} onClick={save}
              className="btn-primary justify-center flex-1 text-sm">
              <FloppyDisk size={12} weight="bold" /> {busy ? "Saving…" : "Save"}
            </button>
            <button onClick={cancelEdit} data-testid="meta-cancel"
              className="btn-ghost justify-center text-sm">
              <X size={12} /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <p className="label mb-1">Last login</p>
            <p className="text-sm" data-testid="meta-last-login" title={client.last_login_at || ""}>
              {client.last_login_at
                ? new Date(client.last_login_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
                : <span style={{ color: "var(--text-dim)" }}>Never signed in</span>}
            </p>
          </div>
          {client.business_name && (
            <div>
              <p className="label mb-1">Business</p>
              <p className="text-sm" data-testid="meta-business-view" style={{ overflowWrap: "anywhere" }}>{client.business_name}</p>
            </div>
          )}
          {client.phone && (
            <div>
              <p className="label mb-1">Phone</p>
              <p className="text-sm" data-testid="meta-phone-view" style={{ overflowWrap: "anywhere" }}>
                <a href={`tel:${client.phone}`} className="underline underline-offset-4">{client.phone}</a>
              </p>
            </div>
          )}
          <div>
            <p className="label mb-1">Email</p>
            <p className="text-sm font-mono" data-testid="meta-email-view" style={{ overflowWrap: "anywhere", wordBreak: "break-all" }}>{client.email}</p>
          </div>
          <div>
            <p className="label mb-1">Est. completion date</p>
            <p className="text-sm" data-testid="meta-eta-view">
              {client.estimated_completion_date || <span style={{ color: "var(--text-dim)" }}>Not set</span>}
            </p>
          </div>
          <div>
            <p className="label mb-1">Client types</p>
            <p className="text-sm" data-testid="meta-type-view">{clientTypeLabel}</p>
          </div>
          {client.upcoming_appointment && (
            <div>
              <p className="label mb-1">Upcoming appointment</p>
              <p className="text-sm" data-testid="meta-upcoming-appt">
                {(() => {
                  const dt = new Date(client.upcoming_appointment.datetime);
                  const s = dt.toLocaleString(undefined, {
                    weekday: "short", month: "short", day: "numeric",
                    hour: "numeric", minute: "2-digit",
                    timeZone: "America/New_York",
                  });
                  const tz = new Intl.DateTimeFormat(undefined, {
                    timeZone: "America/New_York", timeZoneName: "short",
                  }).formatToParts(dt).find(p => p.type === "timeZoneName")?.value || "ET";
                  return `${s} · ${tz}`;
                })()}
              </p>
              <p className="label mt-0.5" style={{ color: "var(--text-dim)" }}>
                {client.upcoming_appointment.type}
              </p>
            </div>
          )}
        </div>
      )}
      <div className="hair mt-4" style={{ height: 1 }} />
      <div className="mt-4 space-y-2">
        <p className="label">Actions</p>
        <button data-testid="send-task-reminder" disabled={busy2} onClick={sendReminder}
          className="btn-ghost justify-center w-full text-xs">
          <PaperPlaneTilt size={12} weight="bold" /> {busy2 ? "Sending…" : "Send task reminder"}
        </button>
        <button data-testid="reset-password" onClick={resetPw}
          className="btn-ghost justify-center w-full text-xs">
          Reset client password
        </button>
      </div>
    </div>
  );
}

// ---------------- Payments section ----------------
function PaymentsSection({ userId, payments, onRefresh }) {
  const [f, setF] = useState({ amount: "", due_date: "", note: "", recurring: "", end_date: "" });
  const add = async (e) => {
    e.preventDefault();
    try {
      const payload = { amount: parseFloat(f.amount), due_date: f.due_date, note: f.note };
      if (f.recurring) { payload.recurring = f.recurring; payload.end_date = f.end_date; }
      const { data } = await api.post(`/admin/clients/${userId}/payments`, payload);
      toast.success(data.added > 1 ? `Scheduled ${data.added} payments` : "Payment scheduled");
      setF({ amount: "", due_date: "", note: "", recurring: "", end_date: "" });
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="admin-payments-section">
      <div className="lg:col-span-3 space-y-4">
        <PaymentCalendar payments={payments} testid="admin-payment-calendar" />
        <div className="panel p-6">
          <h4 className="title-xl text-xl mb-3">Add payment</h4>
          <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input required type="number" step="0.01" placeholder="Amount"
              data-testid="pay-amount"
              value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })}
              className="px-3 py-3" />
            <input required type="date" placeholder={f.recurring ? "Start date" : "Due date"}
              data-testid="pay-date"
              value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })}
              className="px-3 py-3" />
            <input placeholder="Note (e.g. 50% deposit)" value={f.note}
              data-testid="pay-note"
              onChange={(e) => setF({ ...f, note: e.target.value })} className="px-3 py-3 md:col-span-2" />
            <select data-testid="pay-recurring"
              value={f.recurring} onChange={(e) => setF({ ...f, recurring: e.target.value })}
              className="px-3 py-3">
              <option value="">One-time payment</option>
              <option value="weekly">Recurring · Weekly</option>
              <option value="biweekly">Recurring · Bi-weekly</option>
              <option value="monthly">Recurring · Monthly</option>
            </select>
            {f.recurring && (
              <input required={!!f.recurring} type="date" placeholder="Schedule end date"
                data-testid="pay-end-date"
                value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })}
                className="px-3 py-3 md:col-span-3" />
            )}
            <button data-testid="pay-submit" className="btn-primary md:col-span-4 justify-center h-12">
              <Plus size={14} weight="bold" /> {f.recurring ? "Schedule series" : "Schedule"}
            </button>
          </form>
        </div>
      </div>
      <AdminPaymentsList userId={userId} payments={payments} onRefresh={onRefresh} />
    </div>
  );
}

// ---------------- Files section ----------------
function FilesSection({ userId, files, onRefresh, defaultSearch }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  // Jotform search state
  const [jfFirst, setJfFirst] = useState(defaultSearch?.first || "");
  const [jfLast, setJfLast] = useState(defaultSearch?.last || "");
  const [jfEmail, setJfEmail] = useState(defaultSearch?.email || "");
  const [jfPhone, setJfPhone] = useState(defaultSearch?.phone || "");
  const [jfForce, setJfForce] = useState(false);
  const [importing, setImporting] = useState(false);

  // Keep the search boxes in sync with the client's latest name/email/phone.
  // If the admin renames the client (or updates email/phone), the stored
  // client identifiers change — we push those into the search inputs so the
  // next import scans under the CURRENT name. Prior identifiers are still
  // included automatically by the backend via `former_names` / `former_emails`.
  const dfFirst = defaultSearch?.first || "";
  const dfLast = defaultSearch?.last || "";
  const dfEmail = defaultSearch?.email || "";
  const dfPhone = defaultSearch?.phone || "";
  useEffect(() => { setJfFirst(dfFirst); }, [dfFirst]);
  useEffect(() => { setJfLast(dfLast); }, [dfLast]);
  useEffect(() => { setJfEmail(dfEmail); }, [dfEmail]);
  useEffect(() => { setJfPhone(dfPhone); }, [dfPhone]);

  const runImport = async () => {
    if (importing) return;
    setImporting(true);
    const t = toast.loading("Scanning Jotforms — this can take up to a minute…");
    try {
      const { data } = await api.post(
        `/admin/clients/${userId}/import-jotform`,
        { first_name: jfFirst, last_name: jfLast, email: jfEmail, phone: jfPhone, force: jfForce },
        { timeout: 120000 }
      );
      toast.dismiss(t);
      const fresh = data.imported.filter(i => !i.already_imported).length;
      const skipped = data.count - fresh;
      const parts = [];
      if (fresh) parts.push(`${fresh} new`);
      if (skipped) parts.push(`${skipped} already on file`);
      if (data.errors?.length) parts.push(`${data.errors.length} render error(s)`);
      const summary = parts.length ? parts.join(" · ") : "no matches found";
      const scanLine = `Scanned ${data.forms_scanned} form(s), ${data.submissions_scanned ?? "?"} submission(s), matched ${data.submissions_matched ?? data.count}.`;
      const truncNote = data.truncated ? " More matches remain — click Import again to pull the next batch." : "";
      toast.success(`${scanLine} ${summary}.${truncNote} New files are hidden from the client until you toggle them visible.`);
      if (data.errors?.length) {
        console.warn("Jotform import — render errors:", data.errors);
      }
      if (data.search_used) {
        console.log("Jotform import — searched with:", data.search_used);
      }
      onRefresh();
    } catch (e) {
      toast.dismiss(t);
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message || "Import failed";
      toast.error(`Import failed: ${msg}`);
    } finally { setImporting(false); }
  };

  const upload = async () => {
    const list = Array.from(inputRef.current?.files || []);
    if (!list.length) { toast.error("Pick at least one file first"); return; }
    setBusy(true);
    let ok = 0, fail = 0;
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append("file", file); fd.append("note", note);
        await api.post(`/admin/clients/${userId}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch (_e) { fail++; }
    }
    if (ok) toast.success(`Uploaded ${ok} file${ok === 1 ? "" : "s"}`);
    if (fail) toast.error(`${fail} upload${fail === 1 ? "" : "s"} failed`);
    setNote(""); if (inputRef.current) inputRef.current.value = "";
    setBusy(false);
    onRefresh();
  };

  const del = async (fid) => {
    if (!window.confirm("Delete file?")) return;
    await api.delete(`/admin/clients/${userId}/files/${fid}`);
    onRefresh();
  };

  const toggleVisibility = async (f) => {
    const nextHidden = !f.hidden_from_client;
    try {
      await api.patch(`/admin/clients/${userId}/files/${f.file_id}`,
        { hidden_from_client: nextHidden });
      toast.success(nextHidden ? "Hidden from client" : "Now visible to client");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    }
  };

  const dl = async (f) => {
    const res = await api.get(`/files/${f.file_id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = f.original_filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-files-section">
      <div className="panel p-6">
        <h4 className="title-xl text-xl mb-3">Import Jotforms as PDF</h4>
        <p className="text-sm mb-4" style={{ color: "var(--text-dim)" }}>
          Pulls every completed submission that matches ANY of these fields. Imported PDFs start hidden from the client — toggle "Visible" on each file to share.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input placeholder="First name" value={jfFirst} data-testid="jf-first"
            onChange={(e) => setJfFirst(e.target.value)} className="px-3 py-3" />
          <input placeholder="Last name" value={jfLast} data-testid="jf-last"
            onChange={(e) => setJfLast(e.target.value)} className="px-3 py-3" />
          <input placeholder="Email" value={jfEmail} data-testid="jf-email"
            onChange={(e) => setJfEmail(e.target.value)} className="px-3 py-3" />
          <input placeholder="Phone" value={jfPhone} data-testid="jf-phone"
            onChange={(e) => setJfPhone(e.target.value)} className="px-3 py-3" />
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-3">
          <label className="text-sm inline-flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={jfForce}
              data-testid="jf-force"
              onChange={(e) => setJfForce(e.target.checked)} />
            <span>Re-import to refresh existing PDFs (overwrites old copies)</span>
          </label>
          <button data-testid="jotform-import-btn"
            onClick={runImport} disabled={importing}
            className="btn-primary text-sm disabled:opacity-40">
            <FileArrowDown size={14} weight="bold" /> {importing ? "Scanning…" : "Search & Import"}
          </button>
        </div>
      </div>

      <div className="panel p-6">
        <h4 className="title-xl text-xl mb-3">Upload a file to this client</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <FilePicker ref={inputRef} testid="file-input" className="md:col-span-2" />
          <input placeholder="Optional note" value={note} data-testid="file-note"
            onChange={(e) => setNote(e.target.value)} className="px-3 py-3" />
          <button data-testid="file-upload-btn" disabled={busy} onClick={upload}
            className="btn-primary justify-center h-12">
            <UploadSimple size={14} weight="bold" /> {busy ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>

      <div>
        {files.length === 0 ? (
          <div className="panel p-8 text-center label">No files yet</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {files.map((f) => {
              const hidden = !!f.hidden_from_client;
              return (
                <div key={f.file_id} className="panel p-5" data-testid={`admin-file-${f.file_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="title-xl text-lg truncate">{f.original_filename}</p>
                      <p className="label mt-1">{(f.size/1024).toFixed(1)} KB · {new Date(f.uploaded_at).toLocaleDateString()}</p>
                      {f.note && <p className="text-sm mt-2" style={{ color: "var(--text-dim)" }}>{f.note}</p>}
                      <div className="mt-2">
                        <span className="label px-2 py-0.5"
                          data-testid={`admin-file-visibility-${f.file_id}`}
                          style={{
                            background: hidden ? "#fee2e2" : "#dcfce7",
                            color: hidden ? "#991b1b" : "#166534",
                            border: `1px solid ${hidden ? "#fee2e2" : "#dcfce7"}`,
                            fontWeight: 700,
                          }}>
                          {hidden ? "Hidden from client" : "Visible to client"}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button data-testid={`admin-toggle-vis-${f.file_id}`}
                        onClick={() => toggleVisibility(f)}
                        title={hidden ? "Make visible to client" : "Hide from client"}
                        className="btn-ghost text-xs">
                        {hidden
                          ? <Eye size={12} weight="bold" />
                          : <EyeSlash size={12} weight="bold" />}
                      </button>
                      <button data-testid={`admin-dl-${f.file_id}`} onClick={() => dl(f)} className="btn-ghost text-xs">
                        <DownloadSimple size={12} weight="bold" />
                      </button>
                      <button data-testid={`admin-del-file-${f.file_id}`} onClick={() => del(f.file_id)} className="btn-ghost text-xs">
                        <Trash size={12} weight="bold" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Messages section ----------------
function MessagesSection({ userId, messages, onRefresh, clientEmail, clientName }) {
  const [draft, setDraft, { saved: draftSaved, clear: clearDraft }] = useDraft(
    `admin.msg.${userId}`,
    { subject: "", body: "", replyTo: "orders@prettygrlsdesign.com", dueDate: "" }
  );
  const [busy, setBusy] = useState(false);
  const filesRef = useRef(null);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const files = Array.from(filesRef.current?.files || []);
      const file_ids = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("note", "Message attachment");
        fd.append("tag", "message");
        const { data } = await api.post(`/admin/clients/${userId}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data?.file_id) file_ids.push(data.file_id);
      }
      const { data } = await api.post(`/admin/clients/${userId}/messages`, {
        subject: draft.subject, body: draft.body, reply_to: draft.replyTo,
        due_date: draft.dueDate || null,
        file_ids,
      });
      if (data.warning) toast.warning(data.warning);
      else toast.success(`Email sent to ${clientEmail}`);
      clearDraft();
      if (filesRef.current) filesRef.current.value = "";
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admin-messages-section">
      <div className="panel p-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="title-xl text-xl">Compose email</h4>
          {draftSaved && <span className="label text-xs"
            data-testid="admin-msg-draft-saved"
            style={{ color: "var(--text-dim)" }}>Draft saved</span>}
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--text-dim)" }}>Sends to <span style={{ color: "var(--text)" }}>{clientEmail}</span></p>
        <form onSubmit={send} className="space-y-3">
          <MessageTemplatePicker client={{ name: clientName, email: clientEmail }} current={draft}
            onApply={(t) => setDraft({ ...draft, subject: t.subject || draft.subject, body: t.body })} />
          <div>
            <p className="label mb-1">Reply-to</p>
            <select data-testid="msg-reply-to" value={draft.replyTo}
              onChange={(e) => setDraft({ ...draft, replyTo: e.target.value })}
              className="w-full px-4 py-3">
              <option value="orders@prettygrlsdesign.com">orders@prettygrlsdesign.com</option>
              <option value="help@prettygrlsdesign.com">help@prettygrlsdesign.com</option>
            </select>
          </div>
          <input required placeholder="Subject" value={draft.subject}
            data-testid="msg-subject"
            onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
            className="w-full px-4 py-3" />
          <textarea required placeholder="Write your message…" value={draft.body}
            data-testid="msg-body"
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            rows={8} className="w-full px-4 py-3" />
          <FormatBar value={draft.body} onChange={(v) => setDraft({ ...draft, body: v })} target="msg-body" testid="msg-fmt" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="min-w-0">
              <p className="label mb-1">Due date (optional)</p>
              <input type="date" value={draft.dueDate} data-testid="msg-due"
                onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
                className="w-full px-3 py-2" />
            </div>
            <div className="min-w-0">
              <p className="label mb-1">Attachments (optional)</p>
              <FilePicker ref={filesRef} testid="msg-files" />
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <button data-testid="msg-send-btn" disabled={busy || !draft.subject || !draft.body}
              className="btn-primary justify-center flex-1 h-12">
              <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Sending…" : "Send email"}
            </button>
            {(draft.subject || draft.body || draft.dueDate) && (
              <button type="button" onClick={clearDraft}
                data-testid="admin-msg-clear-draft"
                className="btn-ghost text-xs">Clear draft</button>
            )}
          </div>
        </form>
      </div>
      <MessageHistory userId={userId} messages={messages} onRefresh={onRefresh}
        client={{ name: clientName, email: clientEmail }} />
    </div>
  );
}

function MessageHistory({ userId, messages, onRefresh, client }) {
  const [view, setView] = useState("convo");
  const [allOpen, setAllOpen] = useState(null);
  const [q, setQ] = useState("");
  const { system, roots, repliesOf } = splitMessages(messages, q);
  return (
    <div data-testid="admin-message-history">
      <div className="flex justify-between items-center gap-3 flex-wrap mb-3">
        <FilterPills testid="admin-msg-view" value={view} onChange={setView}
          options={[
            { k: "convo", label: `Conversations (${roots.length})` },
            { k: "system", label: `System messages (${system.length})` },
          ]} />
        {view === "convo" && roots.length > 1 && (
          <ExpandAllToggle testid="admin-msgs-expand-all"
            allOpen={allOpen === true} onToggle={(next) => setAllOpen(next)} />
        )}
      </div>
      <div className="mb-3"><MessageSearch value={q} onChange={setQ} testid="admin-msg-search" /></div>
      {view === "system" ? <SystemMessagesList items={system} testid="admin-system-messages" /> : (
        roots.length === 0 ? <div className="panel p-8 text-center label">{q ? "No matching conversations" : "No conversations yet"}</div> : (
          <div className="space-y-3">
            {roots.map(m => (
              <AdminThreadCard key={m.message_id} root={m} replies={repliesOf(m.message_id)} client={client}
                userId={userId} onRefresh={onRefresh}
                forceOpen={allOpen === null ? undefined : allOpen} />
            ))}
          </div>
        )
      )}
    </div>
  );
}

function ThreadMessage({ m }) {
  const inbound = m.direction === "client_to_admin";
  return (
    <div className="mt-3 pl-4 border-l-2" style={{ borderColor: inbound ? "var(--text)" : "var(--hair)" }}
      data-testid={`thread-msg-${m.message_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="sample-badge">{inbound ? "Client" : "PGD"}</span>
        <p className="label">{new Date(m.sent_at).toLocaleString()}</p>
        {m.delivery_status && m.delivery_status !== "sent" && m.delivery_status !== "received" && (
          <span className="label" style={{ color: "#991b1b" }}>not delivered</span>
        )}
      </div>
      <p className="mt-1 whitespace-pre-line text-sm leading-relaxed" style={{ color: "var(--text-dim)" }}><LinkifiedText text={m.body} /></p>
      <MessageFiles m={m} />
    </div>
  );
}

function ThreadSubjectEditor({ userId, m, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(m.subject || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!val.trim() || val.trim() === m.subject) { setEditing(false); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/clients/${userId}/messages/${m.message_id}`, { subject: val.trim() });
      toast.success("Thread renamed");
      setEditing(false);
      await onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  if (!editing) {
    return (
      <button onClick={() => { setVal(m.subject || ""); setEditing(true); }}
        data-testid={`thread-rename-${m.message_id}`} className="btn-ghost text-xs" title="Rename thread" aria-label="Rename thread">
        <PencilSimple size={12} weight="bold" />
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} disabled={busy}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        data-testid={`thread-rename-input-${m.message_id}`} className="px-3 py-2 text-sm w-48 md:w-72" />
      <button onClick={save} disabled={busy} data-testid={`thread-rename-save-${m.message_id}`}
        className="btn-primary text-xs h-9"><Check size={12} weight="bold" /></button>
      <button onClick={() => setEditing(false)} disabled={busy}
        data-testid={`thread-rename-cancel-${m.message_id}`} className="btn-ghost text-xs h-9"><X size={12} weight="bold" /></button>
    </div>
  );
}

function AdminThreadCard({ root: m, replies, userId, onRefresh, forceOpen, client }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const threadClosed = m.thread_status === "handled";
  const isHandled = threadClosed;
  const last = replies[replies.length - 1] || m;
  const needsReply = !threadClosed && last.direction === "client_to_admin";
  const act = async (path, msg, patch) => {
    try {
      if (patch) await api.patch(`/admin/clients/${userId}/messages/${m.message_id}`, patch);
      else await api.post(`/admin/clients/${userId}/messages/${m.message_id}/${path}`);
      toast.success(msg);
      await onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const header = (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <p className="title-xl text-lg" style={{ fontWeight: 700 }} data-testid={`thread-subject-${m.message_id}`}>{m.subject}</p>
        {threadClosed && (
          <span className="sample-badge" data-testid={`thread-closed-badge-${m.message_id}`}
            style={{ background: "#e5e7eb", color: "#374151" }}>Closed</span>
        )}
        {m.pinned && (
          <span className="sample-badge" data-testid={`thread-pinned-badge-${m.message_id}`}
            style={{ background: "#fef3c7", color: "#78350f" }}><PushPin size={10} weight="fill" /> Pinned</span>
        )}
        {needsReply && (
          <span className="sample-badge" style={{ background: "#fee2e2", color: "#991b1b" }}>Needs reply</span>
        )}
      </div>
      <p className="label mt-1">
        {new Date(last.sent_at).toLocaleString()}
        {` · ${replies.length + 1} message${replies.length ? "s" : ""}`}
      </p>
    </div>
  );
  return (
    <AccordionCard testid={`admin-message-${m.message_id}`} header={header} forceOpen={forceOpen}
      anchorId={`msg-${m.message_id}`}
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => act("", m.pinned ? "Thread unpinned" : "Thread pinned", { pinned: !m.pinned })}
            data-testid={`thread-pin-${m.message_id}`} className="btn-ghost text-xs"
            title={m.pinned ? "Unpin thread" : "Pin to top"} aria-label={m.pinned ? "Unpin thread" : "Pin to top"}
            style={m.pinned ? { background: "var(--text)", color: "var(--bg)" } : undefined}>
            <PushPin size={12} weight={m.pinned ? "fill" : "bold"} />
          </button>
          <ThreadSubjectEditor userId={userId} m={m} onRefresh={onRefresh} />
        </div>
      }>
      <ThreadMessage m={m} />
      {replies.map(r => <ThreadMessage key={r.message_id} m={r} />)}
      <div className="mt-4 flex flex-wrap gap-2">
        <button data-testid={`msg-reply-${m.message_id}`} onClick={() => setReplyOpen(!replyOpen)}
          className="btn-ghost text-xs"
          title={isHandled ? "Replying will reopen this thread" : "Send a reply the client will receive via email"}>
          <PaperPlaneTilt size={12} weight="bold" /> {replyOpen ? "Hide reply" : "Reply"}
        </button>
        <button data-testid={`msg-handle-${m.message_id}`}
          onClick={() => act(isHandled ? "unhandle" : "handle", isHandled ? "Thread reopened" : "Thread marked handled")}
          className="btn-ghost text-xs">
          {isHandled ? <><X size={12} weight="bold" /> Reopen thread</> : <><CheckCircle size={12} weight="bold" /> Mark handled</>}
        </button>
        <button data-testid={`msg-notify-${m.message_id}`} onClick={() => act("notify", "Client notified via email")}
          className="btn-ghost text-xs" title="Send the client a 'your request has been handled' email">
          <ClockClockwise size={12} weight="bold" /> Notify client
        </button>
      </div>
      {replyOpen && (
        <AdminThreadReplyBox userId={userId} rootId={m.message_id} rootSubject={m.subject} client={client}
          onSent={async () => { setReplyOpen(false); await onRefresh(); }} />
      )}
    </AccordionCard>
  );
}

const downloadFile = async (f) => {
  const res = await api.get(`/files/${f.file_id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = f.original_filename; a.click();
  URL.revokeObjectURL(url);
};

// ---------------- Samples (admin) ----------------
function SamplesAdminSection({ userId, messages, files, onRefresh }) {
  const samples = messages.filter(m => m.sample);
  const edits = messages.filter(m => m.sample_edits);
  const editsFor = (mid) => edits.filter(e => e.reply_to_message_id === mid);
  const [allOpen, setAllOpen] = useState(null);
  return (
    <div className="space-y-6" data-testid="admin-samples-section">
      <SampleComposer userId={userId} onDone={onRefresh} />
      <div className="space-y-4">
        <div className="flex justify-between items-center gap-3 flex-wrap">
          <h3 className="title-xl text-2xl">Sent samples · {samples.length}</h3>
          {samples.length > 1 && (
            <ExpandAllToggle testid="admin-samples-expand-all"
              allOpen={allOpen === true}
              onToggle={(next) => setAllOpen(next)} />
          )}
        </div>
        {samples.length === 0 && (
          <div className="panel p-8 text-center label">No samples sent yet</div>
        )}
        {samples.map((s) => {
          const sFiles = sampleFilesOf(s, files);
          const clientEdits = editsFor(s.message_id);
          const status = s.sample_status || "pending";
          const statusMap = {
            pending:         { bg: "#fef3c7", color: "#78350f", label: "Awaiting client" },
            approved:        { bg: "#d1fae5", color: "#065f46", label: "Project approved" },
            declined:        { bg: "#fee2e2", color: "#991b1b", label: "Declined" },
            edits_requested: { bg: "#dbeafe", color: "#1e3a8a", label: "Pending update" },
          };
          const st = statusMap[status] || statusMap.pending;
          const header = (
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="sample-badge"><Sparkle size={10} weight="fill" /> Sample</span>
                <span className="label px-2 py-0.5" style={{ background: st.bg, color: st.color, border: `1px solid ${st.bg}` }}>{st.label}</span>
                {status === "pending" && <SampleDueBadge dueDate={s.due_date} />}
                {s.allow_edits === false && <span className="label px-2 py-0.5" style={{ background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb" }}>Edits disabled</span>}
                <p className="title-xl text-lg" style={{ fontWeight: 700 }}>{s.subject.replace(/^\[Sample\]\s*/, "")}</p>
              </div>
              <p className="label mt-1">
                {new Date(s.sent_at).toLocaleString()}
                {clientEdits.length > 0 && ` · ${clientEdits.length} client edit${clientEdits.length === 1 ? "" : "s"}`}
              </p>
            </div>
          );
          return (
            <AccordionCard key={s.message_id} testid={`admin-sample-${s.message_id}`}
              header={header} anchorId={`sample-${s.message_id}`}
              forceOpen={allOpen === null ? undefined : allOpen}>
              <SampleEditor sample={s} userId={userId} onDone={onRefresh} />
              {s.sample_url && <a href={s.sample_url} target="_blank" rel="noreferrer" className="label underline underline-offset-4 mt-2 inline-block break-all">{s.sample_url}</a>}
              {sFiles.length > 0 && (
                <p className="label mt-2" data-testid={`admin-sample-files-${s.message_id}`}>
                  Attached file{sFiles.length > 1 ? "s" : ""}: {sFiles.map(x => x.original_filename).join(", ")}
                </p>
              )}
              <SampleGallery files={sFiles} onDownload={downloadFile} testid={`admin-sample-gallery-${s.message_id}`} />
              {status === "approved" && s.approved_by_admin && (
                <div className="mt-3 hair p-3" data-testid={`admin-sample-manual-note-${s.message_id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="label" style={{ color: "#065f46" }}>Approval note · by {s.approved_by_admin}</p>
                    <UndoApprovalButton sample={s} userId={userId} onDone={onRefresh} />
                  </div>
                  <p className="text-sm whitespace-pre-line">{s.decision_note}</p>
                </div>
              )}
              {status === "declined" && s.decision_note && (
                <div className="mt-3 hair p-3">
                  <p className="label mb-1" style={{ color: "#991b1b" }}>Decline reason</p>
                  <p className="text-sm whitespace-pre-line">{s.decision_note}</p>
                </div>
              )}
              {clientEdits.length > 0 && (
                <div className="mt-4 hair p-4">
                  <p className="label mb-2">Client edits · {clientEdits.length}</p>
                  <div className="space-y-2">
                    {clientEdits.map((e) => (
                      <div key={e.message_id} className="text-sm border-l-2 pl-3" style={{ borderColor: "var(--hair)" }}>
                        <p className="label" style={{ color: "var(--text-dim)" }}>{new Date(e.sent_at).toLocaleString()}</p>
                        <p className="whitespace-pre-line mt-1">{e.body}</p>
                        {(e.file_ids || []).map(id => files.find(f => f.file_id === id)).filter(Boolean).map(f => (
                          <button key={f.file_id} type="button" onClick={() => downloadFile(f)} className="btn-ghost text-[11px] h-7 mt-1 mr-2"
                            data-testid={`edit-file-${f.file_id}`}><DownloadSimple size={11} weight="bold" /> {f.original_filename}</button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(["declined", "edits_requested"].includes(status) || (s.notes || []).length > 0) && (
                <TaskNotes taskId={s.message_id} role="admin" anchorId={`sample-${s.message_id}`}
                  basePath={`/admin/clients/${userId}/samples/${s.message_id}/notes`}
                  initialNotes={s.notes || []} label="Comment on this sample" testid="admin-sample-notes"
                  uploadPath={`/admin/clients/${userId}/files`} />
              )}
            </AccordionCard>
          );
        })}
      </div>
    </div>
  );
}

function UndoApprovalButton({ sample, userId, onDone }) {
  const [busy, setBusy] = useState(false);
  const undo = async () => {
    if (!window.confirm("Undo this approval? The sample goes back to how it was and the client isn't notified.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/clients/${userId}/samples/${sample.message_id}/undo-approval`);
      toast.success("Approval undone");
      await onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Undo failed");
    } finally { setBusy(false); }
  };
  return (
    <button disabled={busy} onClick={undo} data-testid={`sample-undo-approval-${sample.message_id}`}
      className="btn-ghost text-xs disabled:opacity-40">
      <ArrowCounterClockwise size={12} weight="bold" /> {busy ? "Undoing…" : "Undo approval"}
    </button>
  );
}

function ManualApproveForm({ sample, userId, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/clients/${userId}/samples/${sample.message_id}/approve`, { note });
      if (data.email === "sent") toast.success("Sample approved — client emailed");
      else toast.warning("Sample approved, but the email could not be sent");
      await onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Approval failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 hair p-4 space-y-3" data-testid={`sample-manual-approve-form-${sample.message_id}`}>
      <p className="label">The client will see <b>"Manually approved by PGD"</b> and get an email.</p>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
        data-testid={`sample-manual-approve-note-${sample.message_id}`}
        placeholder="Optional note, e.g. approved over the phone" className="w-full px-3 py-2 text-sm" />
      <button disabled={busy} onClick={approve} data-testid={`sample-manual-approve-submit-${sample.message_id}`}
        className="btn-primary text-xs disabled:opacity-40">
        <CheckCircle size={12} weight="bold" /> {busy ? "Approving…" : "Approve sample"}
      </button>
    </div>
  );
}

function SampleEditor({ sample, userId, onDone }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(sample.sample_title || sample.subject.replace(/^\[Sample\]\s*/, ""));
  const [description, setDescription] = useState(sample.sample_description || "");
  const [url, setUrl] = useState(sample.sample_url || "");
  const [dueDate, setDueDate] = useState((sample.due_date || "").slice(0, 10));
  const [allowEdits, setAllowEdits] = useState(sample.allow_edits !== false);
  const [editsChannel, setEditsChannel] = useState(sample.edits_channel || "comment");
  const [previewPassword, setPreviewPassword] = useState(sample.preview_password || "");
  const [busy, setBusy] = useState(false);
  const [nudging, setNudging] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const nudge = async () => {
    setNudging(true);
    try {
      const { data } = await api.post(`/admin/clients/${userId}/reminder-sample/${sample.message_id}`);
      if (data.sent) toast.success("Reminder sent");
      else toast.warning(data.error || "Reminder could not be sent");
      await onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send reminder");
    } finally { setNudging(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/clients/${userId}/samples/${sample.message_id}`, {
        title, description, url, allow_edits: allowEdits, edits_channel: editsChannel,
        due_date: dueDate || "", preview_password: previewPassword || "",
      });
      toast.success("Sample updated");
      setOpen(false);
      await onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    } finally { setBusy(false); }
  };
  const toggleAllow = async (next) => {
    setAllowEdits(next);
    try {
      await api.patch(`/admin/clients/${userId}/samples/${sample.message_id}`, { allow_edits: next });
      toast.success(next ? "Edits enabled" : "Edits disabled");
      await onDone();
    } catch (_e) { setAllowEdits(!next); toast.error("Failed"); }
  };
  return (
    <div className="mb-3">
      <p className="whitespace-pre-line text-sm leading-relaxed" style={{ color: "var(--text-dim)" }}>
        {(sample.body || "").replace(/\n*Link:\s*https?:\/\/\S+\s*$/, "").trim()}
      </p>
      <p className="label mt-2" style={{ color: "var(--text-dim)" }}>
        Client sends edits via: <b>{{
          jotform: "Jotform form",
          none: "No edits — additional edits are +$100",
        }[sample.edits_channel] || "Comment box"}</b>
        {sample.preview_password && (
          <span data-testid={`sample-password-indicator-${sample.message_id}`}
            style={{ marginLeft: 12 }}>
            · Preview password: <b>{sample.preview_password}</b>
          </span>
        )}
      </p>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <button data-testid={`sample-edit-toggle-${sample.message_id}`}
          onClick={() => setOpen(o => !o)} className="btn-ghost text-xs">
          <PencilSimple size={12} weight="bold" /> {open ? "Cancel edit" : "Edit sample"}
        </button>
        <button data-testid={`sample-allow-edits-toggle-${sample.message_id}`}
          onClick={() => toggleAllow(!allowEdits)} className="btn-ghost text-xs">
          {allowEdits ? <><X size={12} weight="bold" /> Remove "Send in edits"</> : <><CheckCircle size={12} weight="bold" /> Allow "Send in edits"</>}
        </button>
        {(sample.sample_status || "pending") === "pending" && (
          <button data-testid={`sample-nudge-${sample.message_id}`}
            disabled={nudging}
            onClick={nudge}
            className="btn-ghost text-xs disabled:opacity-40">
            <PaperPlaneTilt size={12} weight="bold" /> {nudging ? "Sending…" : "Send reminder"}
          </button>
        )}
        {(sample.sample_status || "pending") !== "approved" && (
          <button data-testid={`sample-manual-approve-toggle-${sample.message_id}`}
            onClick={() => setApproveOpen(o => !o)} className="btn-ghost text-xs">
            <CheckCircle size={12} weight="bold" /> {approveOpen ? "Cancel approval" : "Approve for client"}
          </button>
        )}
        {sample.last_reminded_at && (
          <span className="label" style={{ color: "var(--text-dim)" }}>
            Last nudged {new Date(sample.last_reminded_at).toLocaleString()}
          </span>
        )}
      </div>
      {approveOpen && <ManualApproveForm sample={sample} userId={userId} onDone={async () => { setApproveOpen(false); await onDone(); }} />}
      {open && (
        <div className="mt-4 space-y-3">
          <input value={title} data-testid={`sample-edit-title-${sample.message_id}`}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Sample title" className="w-full px-3 py-3" />
          <textarea value={description} data-testid={`sample-edit-desc-${sample.message_id}`}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description" rows={3} className="w-full px-3 py-3" />
          <input value={url} data-testid={`sample-edit-url-${sample.message_id}`}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://link-to-sample (optional)" className="w-full px-3 py-3" />
          <div>
            <p className="label mb-1">Due date</p>
            <input type="date" value={dueDate}
              data-testid={`sample-edit-due-${sample.message_id}`}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-3" />
          </div>
          <div>
            <p className="label mb-1">Edits channel</p>
            <select data-testid={`sample-edit-channel-${sample.message_id}`}
              value={editsChannel} onChange={(e) => setEditsChannel(e.target.value)}
              className="w-full px-3 py-2">
              <option value="comment">Comment box (in-app textarea)</option>
              <option value="jotform">Jotform edit form (external link)</option>
              <option value="none">No edits (included round complete)</option>
            </select>
          </div>
          <div>
            <p className="label mb-1">Preview password (leave blank to remove)</p>
            <input type="text" value={previewPassword}
              data-testid={`sample-edit-password-${sample.message_id}`}
              onChange={(e) => setPreviewPassword(e.target.value)}
              placeholder="No password required"
              className="w-full px-3 py-3" />
          </div>
          <button data-testid={`sample-edit-save-${sample.message_id}`}
            disabled={busy} onClick={save} className="btn-primary text-xs">
            <FloppyDisk size={12} weight="bold" /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      )}
    </div>
  );
}
function SampleComposer({ userId, onDone }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notify, setNotify] = useState(true);
  const [editsChannel, setEditsChannel] = useState("comment"); // "comment" | "jotform"
  const [previewPassword, setPreviewPassword] = useState("");
  const [includePasswordInEmail, setIncludePasswordInEmail] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/sample-templates").then(r => setTemplates(r.data)).catch(() => {});
  }, []);
  const applyTpl = (tid) => {
    setSelectedTpl(tid);
    const t = templates.find(x => x.template_id === tid);
    if (!t) return;
    setTitle(t.title || "");
    setDescription(t.description || "");
    setUrl(t.url || "");
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!title) { toast.error("Title required"); return; }
    const picked = Array.from(inputRef.current?.files || []);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title); fd.append("description", description);
      fd.append("url", url); fd.append("notify", String(notify));
      fd.append("edits_channel", editsChannel);
      fd.append("due_date", dueDate || "");
      fd.append("preview_password", previewPassword || "");
      fd.append("include_password_in_email", String(!!previewPassword && includePasswordInEmail && notify));
      picked.forEach(f => fd.append("files", f));
      const { data } = await api.post(`/admin/clients/${userId}/sample`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.delivery_status === "failed") toast.warning("Sample stored but email failed");
      else toast.success("Sample sent");
      setTitle(""); setDescription(""); setUrl(""); setDueDate(""); setPreviewPassword("");
      setSelectedTpl(""); setEditsChannel("comment"); setIncludePasswordInEmail(true);
      if (inputRef.current) inputRef.current.value = "";
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="panel p-6" data-testid="sample-composer">
      <h4 className="title-xl text-xl mb-1">Send a sample</h4>
      <p className="text-sm mb-3" style={{ color: "var(--text-dim)" }}>Upload preview files and/or paste a link (Figma, Dropbox, etc) — or send just a title and description. Client gets an email notification.</p>
      {templates.length > 0 && (
        <div className="mb-3">
          <p className="label mb-1">Apply template</p>
          <select data-testid="sample-tpl-select"
            value={selectedTpl} onChange={(e) => applyTpl(e.target.value)}
            className="w-full px-3 py-2">
            <option value="">— None —</option>
            {templates.map(t => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
          </select>
        </div>
      )}
      <form onSubmit={submit} className="space-y-3">
        <input required placeholder="Sample title (e.g. Logo v2 preview)" value={title}
          data-testid="sample-title" onChange={(e) => setTitle(e.target.value)} className="w-full px-3 py-3" />
        <textarea placeholder="Optional description" value={description}
          data-testid="sample-desc" onChange={(e) => setDescription(e.target.value)}
          rows={2} className="w-full px-3 py-3" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input placeholder="https://link-to-sample" value={url}
            data-testid="sample-url" onChange={(e) => setUrl(e.target.value)} className="px-3 py-3" />
          <FilePicker ref={inputRef} testid="sample-file" />
        </div>
        <div>
          <p className="label mb-1">Due date (optional)</p>
          <input type="date" value={dueDate}
            data-testid="sample-due-date"
            onChange={(e) => setDueDate(e.target.value)}
            className="w-full px-3 py-3" />
          <p className="label mt-1" style={{ color: "var(--text-dim)" }}>
            When the client should approve or send edits by.
          </p>
        </div>
        <div>
          <p className="label mb-1">Where should the client send edits?</p>
          <select data-testid="sample-edits-channel"
            value={editsChannel} onChange={(e) => setEditsChannel(e.target.value)}
            className="w-full px-3 py-2">
            <option value="comment">Comment box (in-app textarea)</option>
            <option value="jotform">Jotform edit form (external link)</option>
            <option value="none">No edits (included round complete)</option>
          </select>
          <p className="label mt-1" style={{ color: "var(--text-dim)" }}>
            {editsChannel === "none"
              ? "Client will see a 'Request additional edits ($100)' button instead of the normal edit UI."
              : "The client will only see this one option on the sample."}
          </p>
        </div>
        <div>
          <p className="label mb-1">Preview password (optional)</p>
          <input type="text" value={previewPassword}
            data-testid="sample-preview-password"
            placeholder="Leave blank for no password"
            onChange={(e) => setPreviewPassword(e.target.value)}
            className="w-full px-3 py-3" />
          <p className="label mt-1" style={{ color: "var(--text-dim)" }}>
            If set, the client must enter this password to view the sample.
          </p>
          {previewPassword && notify && (
            <label className="flex items-center gap-2 text-sm cursor-pointer mt-2"
              data-testid="sample-include-password-label">
              <input type="checkbox" checked={includePasswordInEmail}
                data-testid="sample-include-password"
                onChange={(e) => setIncludePasswordInEmail(e.target.checked)}
                className="w-4 h-4" />
              Include the password in the notification email
            </label>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={notify} data-testid="sample-notify"
            onChange={(e) => setNotify(e.target.checked)} className="w-4 h-4" />
          Send email notification to client
        </label>
        <button data-testid="sample-submit" disabled={busy} className="btn-primary justify-center w-full h-12">
          <Sparkle size={14} weight="bold" /> {busy ? "Sending…" : "Send sample"}
        </button>
      </form>
    </div>
  );
}

// ---------------- Main page ----------------
export default function AdminClientDetail() {
  const { user_id } = useParams();
  const cacheKey = `admin-client-${user_id}`;
  const cached = getCached(cacheKey);
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [justCreated, setJustCreated] = useState(location.state?.created || null);
  const [c, setC] = useState(cached?.c ?? null);
  const [messages, setMessages] = useState(cached?.messages ?? []);
  const [files, setFiles] = useState(cached?.files ?? []);
  const [adminTasks, setAdminTasks] = useState(cached?.adminTasks ?? []);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(location.search).get("tab");
    return p || "required";
  });

  // Sync tab -> URL and back
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("tab") !== tab) {
      const hadTab = p.has("tab");
      p.set("tab", tab);
      navigate({ search: `?${p.toString()}`, hash: "" }, { replace: !hadTab });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  useEffect(() => {
    const p = new URLSearchParams(location.search).get("tab");
    if (p && p !== tab) setTab(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const load = async () => {
    try {
      const [cli, msg, fls, at] = await Promise.all([
        api.get(`/admin/clients/${user_id}`),
        api.get(`/admin/clients/${user_id}/messages`),
        api.get(`/admin/clients/${user_id}/files`),
        api.get(`/admin/tasks`, { params: { client_user_id: user_id } }),
      ]);
      setC(cli.data); setMessages(msg.data); setFiles(fls.data);
      setAdminTasks(at.data || []);
      setCached(cacheKey, {
        c: cli.data, messages: msg.data, files: fls.data, adminTasks: at.data || [],
      });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, [user_id]);

  const verifyAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/clients/${user_id}/verify`);
      setC(data);
      toast.success("Verified via connected sources");
    } catch (_e) { toast.error("Verification failed"); }
    finally { setBusy(false); }
  };

  const toggleOff = async () => {
    try {
      if (c.offboarding_enabled) {
        await api.post(`/admin/clients/${user_id}/offboarding/disable`);
        toast.success("Offboarding disabled");
      } else {
        await api.post(`/admin/clients/${user_id}/offboarding/enable`);
        toast.success("Offboarding enabled");
      }
      load();
    } catch (_e) { toast.error("Failed"); }
  };

  if (err) return <div className="p-12 label" data-testid="admin-client-error">{err}</div>;
  if (!c) return (
    <div className="min-h-screen overflow-x-hidden" data-testid="admin-client-detail-shell">
      <header className="flex flex-wrap items-center justify-between gap-4 px-6 md:px-6 py-6 border-b"
        style={{ borderColor: "var(--hair)" }}>
        <NavButtons home="/admin" testid="back-to-admin" />
      </header>
      <PageLoader label="Loading client…" testid="admin-client-loader" />
    </div>
  );

  // Build unified required actions for this client (client-side + agency-side)
  const todayIso = new Date().toISOString().slice(0, 10);
  const requiredItems = [];
  for (const t of c.tasks || []) {
    if (t.status === "pending" && !t.archived) {
      requiredItems.push({ key: `onb-${t.task_id}`, kind: "Onboarding", title: t.title, due_date: t.due_date || null, actor: "client", link: `/admin/clients/${user_id}?tab=onboarding` });
    } else if (t.status === "needs_verification" && !t.archived) {
      requiredItems.push({ key: `ver-${t.task_id}`, kind: "Verification", title: `Verify: ${t.title}`, due_date: t.due_date || null, actor: "agency", link: `/admin/clients/${user_id}?tab=onboarding` });
    }
  }
  for (const t of c.offboarding_tasks || []) {
    if (t.status === "pending" && !t.archived) {
      requiredItems.push({ key: `off-${t.task_id}`, kind: "Offboarding", title: t.title, due_date: t.due_date || null, actor: "client", link: `/admin/clients/${user_id}?tab=offboarding` });
    } else if (t.status === "needs_verification" && !t.archived) {
      requiredItems.push({ key: `offver-${t.task_id}`, kind: "Verification", title: `Verify: ${t.title}`, due_date: t.due_date || null, actor: "agency", link: `/admin/clients/${user_id}?tab=offboarding` });
    }
  }
  for (const p of c.payments || []) {
    if (p.status !== "paid" && !p.archived && p.due_date && p.due_date <= todayIso) {
      requiredItems.push({ key: `pay-${p.payment_id}`, kind: "Payment", title: `$${Number(p.amount).toFixed(2)} · ${p.note || "Invoice"}`, due_date: p.due_date, actor: "client", link: `/admin/clients/${user_id}?tab=payments` });
    }
  }
  for (const m of messages || []) {
    if (!m.sample) continue;
    const st = m.sample_status || "pending";
    const title = m.sample_title || (m.subject || "").replace(/^\[Sample\]\s*/, "");
    if (st === "pending") {
      requiredItems.push({ key: `smp-${m.message_id}`, kind: "Sample", title: `Awaiting decision: ${title}`, due_date: m.due_date || null, actor: "client", link: `/admin/clients/${user_id}?tab=samples` });
    } else if (st === "edits_requested") {
      requiredItems.push({ key: `smpe-${m.message_id}`, kind: "Sample", title: `Client requested edits: ${title}`, due_date: m.due_date || null, actor: "agency", link: `/admin/clients/${user_id}?tab=samples` });
    }
  }
  for (const m of messages || []) {
    if (m.direction !== "client_to_admin") continue;
    if (m.sample || m.sample_edits) continue;
    if (m.agency_replied) continue;
    requiredItems.push({ key: `msg-${m.message_id}`, kind: "Admin task", title: `Reply to: ${m.subject || "(no subject)"}`, due_date: null, actor: "agency", link: `/admin/clients/${user_id}?tab=messages` });
  }
  for (const t of adminTasks || []) {
    if (t.status === "complete" || t.archived) continue;
    const actor = t.assignee_role === "client" ? "client" : "agency";
    requiredItems.push({ key: `atk-${t.task_id}`, kind: "Admin task", title: t.title, due_date: t.due_date || null, actor, link: `/admin/tasks` });
  }

  const requiredCount = requiredItems.length;
  const tabs = [
    { key: "required", label: "Pending", count: requiredCount },
    { key: "onboarding", label: "Tasks", count: c.tasks.filter(t => !t.archived).length },
    { key: "payments", label: "Payments", count: (c.payments || []).filter(p => !p.archived).length },
    ...(((c.client_type || "").toLowerCase() === "marketing")
      ? [{ key: "marketing", label: "Marketing" }] : []),
    { key: "samples", label: "Samples", count: messages.filter(m => m.sample).length },
    { key: "files", label: "Files", count: files.length },
    { key: "messages", label: "Messages", count: (({ roots, system }) => roots.length + system.length)(splitMessages(messages)) },
    { key: "notes", label: "Notes", count: (c.notes || []).length },
    ...(c.offboarding_enabled ? [{ key: "offboarding", label: "Offboarding", count: c.offboarding_tasks.filter(t => !t.archived).length }] : []),
  ];

  // Unknown/legacy ?tab= values (e.g. "checklist" from older emails) fall back to a real tab.
  if (!tabs.some(t => t.key === tab)) setTab(tab === "checklist" ? "onboarding" : "required");

  const archive = async () => {
    if (!window.confirm(`${c.archived ? "Unarchive" : "Archive"} ${c.name}?`)) return;
    try {
      await api.post(`/admin/clients/${user_id}/${c.archived ? "unarchive" : "archive"}`);
      toast.success(c.archived ? "Unarchived" : "Archived");
      load();
    } catch (_e) { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen overflow-x-hidden" data-testid="admin-client-detail">
      {justCreated && (
        <CreatedCredentialsModal result={justCreated}
          onClose={() => setJustCreated(null)} />
      )}
      <header className="flex flex-wrap items-center justify-between gap-4 px-6 md:px-6 py-6 border-b" style={{ borderColor: "var(--hair)" }}>
        <div className="flex items-center gap-4">
          <NavButtons home="/admin" testid="back-to-admin" />
          <div>
            <p className="label">Client</p>
            <h1 className="title-xl text-3xl md:text-4xl mt-1" data-testid="client-name">{c.name}</h1>
            {c.business_name && (
              <p className="text-sm mt-1 font-medium" data-testid="client-business-name"
                style={{ color: "var(--text)" }}>{c.business_name}</p>
            )}
            <p className="text-sm mt-1" style={{ color: "var(--text-dim)" }}>{c.email}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="theme-toggle-detail" onClick={toggle} className="btn-ghost p-2" title="Toggle theme">
            {theme === "dark" ? <Sun size={14} weight="bold" /> : <Moon size={14} weight="bold" />}
          </button>
          <button data-testid="admin-toggle-offboarding" onClick={toggleOff} className="btn-ghost">
            <DoorOpen size={14} weight="bold" /> {c.offboarding_enabled ? "Disable" : "Enable"} offboarding
          </button>
          <button data-testid="admin-verify-all" onClick={verifyAll} disabled={busy} className="btn-ghost">
            <ShieldCheck size={14} weight="bold" /> {busy ? "Checking…" : "Verify all"}
          </button>
          <button data-testid="admin-archive-btn" onClick={archive} className="btn-ghost">
            <Trash size={14} weight="bold" /> {c.archived ? "Unarchive" : "Archive"}
          </button>
        </div>
      </header>

      <div className="px-6 md:px-6 pt-6">
        <Tabs tabs={tabs} active={tab} onChange={setTab} testid="admin-client-tabs" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-6 md:p-6 pt-6">
        <div className="lg:col-span-9">
          {tab === "required" && (
            <div className="space-y-4" data-testid="admin-required-tab">
              <div>
                <h3 className="title-xl text-2xl">Pending · {requiredCount}</h3>
                <p className="label mt-1">Everything due for {c.name}, grouped by who owns the next step.</p>
              </div>
              <RequiredActionsPanel items={requiredItems} groupByActor testid="admin-required" />
            </div>
          )}
          {tab === "onboarding" && (
            <TasksSection userId={user_id} tasks={c.tasks} sectionKey="onboarding" onRefresh={load} />
          )}
          {tab === "offboarding" && (
            <TasksSection userId={user_id} tasks={c.offboarding_tasks} sectionKey="offboarding" onRefresh={load} />
          )}
          {tab === "payments" && (
            <PaymentsSection userId={user_id} payments={c.payments || []} onRefresh={load} />
          )}
          {tab === "marketing" && (
            <MarketingSection userId={user_id} role="admin" />
          )}
          {tab === "samples" && (
            <SamplesAdminSection userId={user_id} messages={messages} files={files} onRefresh={load} />
          )}
          {tab === "files" && (
            <FilesSection userId={user_id} files={files} onRefresh={load}
              defaultSearch={{
                first: (c.name || "").trim().split(/\s+/)[0] || "",
                last: (c.name || "").trim().split(/\s+/).slice(1).join(" ") || "",
                email: c.email || "",
                phone: c.phone || "",
              }} />
          )}
          {tab === "messages" && (
            <MessagesSection userId={user_id} messages={messages.filter(m => !m.sample && !m.sample_edits)} clientEmail={c.email} clientName={c.name} onRefresh={load} />
          )}
          {tab === "notes" && (
            <NotesSection userId={user_id} />
          )}
        </div>
        <aside className="lg:col-span-3 order-first lg:order-last space-y-6">
          {isProjectStatusEligible(c) && (
            <ProjectStatusBox client={c} userId={user_id} editable onSaved={load}
              testid="admin-project-status" />
          )}
          <ClientMeta userId={user_id} client={c} onSaved={load} />
        </aside>
      </div>
    </div>
  );
}
